import { useEffect, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import { SendEmail } from '../../services/SendEmail'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'services/analyticsService'
import './Contact.css'

const Contact = () => {
  const formRef = useRef()
  const hasStartedRef = useRef(false)
  const { t } = useTranslation()

  const [form, setForm] = useState({
    reason: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  })

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    trackEvent({
      event: 'form_view',
      form_name: 'contact',
      page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    })
  }, [])

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = (error) => reject(error)
    })
  }

  const handleChange = (e) => {
    if (!hasStartedRef.current && String(e.target.value || '').trim() !== '') {
      hasStartedRef.current = true
      trackEvent({
        event: 'form_start',
        form_name: 'contact',
        form_reason: form.reason || 'not_selected',
      })
    }

    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]

    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        trackEvent({
          event: 'form_validation_error',
          form_name: 'contact',
          form_reason: form.reason || 'not_selected',
          error_type: 'invalid_file_type',
        })
        Swal.fire(
          t('contact_page.alerts.errorTitle'),
          t('contact_page.alerts.pdfOnly'),
          'error',
        )
        e.target.value = ''
        setFile(null)
        return
      }

      if (selectedFile.size > 5000000) {
        trackEvent({
          event: 'form_validation_error',
          form_name: 'contact',
          form_reason: form.reason || 'not_selected',
          error_type: 'file_too_large',
        })
        Swal.fire(
          t('contact_page.alerts.errorTitle'),
          t('contact_page.alerts.fileMax5mb'),
          'error',
        )
        e.target.value = ''
        setFile(null)
        return
      }
      setFile(selectedFile)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    trackEvent({
      event: 'form_submit_attempt',
      form_name: 'contact',
      form_reason: form.reason || 'not_selected',
      has_file: Boolean(file),
    })

    if (!form.reason) {
      Swal.fire(
        t('contact_page.alerts.errorTitle'),
        t('contact_page.alerts.selectReason'),
        'error',
      )
      return
    }
    if (!form.name || !form.email) {
      Swal.fire(
        t('contact_page.alerts.errorTitle'),
        t('contact_page.alerts.nameEmailRequired'),
        'error',
      )
      return
    }
    if (form.reason === 'contact' && !form.message) {
      Swal.fire(
        t('contact_page.alerts.errorTitle'),
        t('contact_page.alerts.typeMessage'),
        'error',
      )
      return
    }
    if (form.reason === 'bancoDeTalentos' && !file) {
      trackEvent({
        event: 'form_validation_error',
        form_name: 'contact',
        form_reason: form.reason,
        error_type: 'missing_cv',
      })
      Swal.fire(
        t('contact_page.alerts.errorTitle'),
        t('contact_page.alerts.attachCvPdf'),
        'error',
      )
      return
    }

    try {
      setLoading(true)

      let fileBase64 = ''
      if (file) {
        fileBase64 = await getBase64(file)
      }

      await SendEmail(form.reason, form, file, fileBase64)

      trackEvent({
        event: 'form_submit_success',
        form_name: 'contact',
        form_reason: form.reason,
        has_file: Boolean(file),
      })

      Swal.fire(
        t('contact_page.alerts.successTitle'),
        t('contact_page.alerts.sentSuccess'),
        'success',
      )

      setForm({
        reason: '',
        name: '',
        email: '',
        phone: '',
        message: '',
      })
      setFile(null)
      hasStartedRef.current = false
      if (formRef.current) formRef.current.reset()
    } catch (error) {
      console.error('Erro ao enviar e-mail:', error)
      trackEvent({
        event: 'form_submit_error',
        form_name: 'contact',
        form_reason: form.reason || 'not_selected',
        error_source: 'send_email',
      })
      Swal.fire(
        t('contact_page.alerts.errorTitle'),
        t('contact_page.alerts.sendFailed'),
        'error',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="contact">
      <header className="contact-header">
        <h2>{t('contact_page.title')}</h2>
        <p>{t('contact_page.subtitle')}</p>
      </header>

      <div className="contact-content">
        <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
          <select
            name="reason"
            value={form.reason}
            onChange={handleChange}
            required
            className="styledSelect"
          >
            <option value="" disabled>
              {t('contact_page.form.reasonPlaceholder')}
            </option>
            <option value="bancoDeTalentos">
              {t('contact_page.form.reasons.bancoDeTalentos')}
            </option>
            <option value="contact">{t('contact_page.form.reasons.contact')}</option>
          </select>

          <input
            type="text"
            name="name"
            placeholder={t('contact_page.form.namePlaceholder')}
            value={form.name}
            onChange={handleChange}
            required
          />

          <div className="row2">
            <input
              type="email"
              name="email"
              placeholder={t('contact_page.form.emailPlaceholder')}
              value={form.email}
              onChange={handleChange}
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder={t('contact_page.form.phonePlaceholder')}
              value={form.phone}
              onChange={handleChange}
            />
          </div>

          <textarea
            name="message"
            placeholder={t('contact_page.form.messagePlaceholder')}
            value={form.message}
            onChange={handleChange}
            required={form.reason === 'contact'}
            className="styledTextarea"
          />

          {form.reason === 'bancoDeTalentos' && (
            <div className="fileUploadContainer">
              <label htmlFor="attachment">{t('contact_page.form.attachmentLabel')}</label>
              <input
                type="file"
                id="attachment"
                name="attachment"
                accept="application/pdf"
                onChange={handleFileChange}
                className="styledFileInput"
              />
              {file && <p className="fileNameSelected">{file.name}</p>}
            </div>
          )}

          <div className="ButtonArea">
            <button
              type="submit"
              disabled={loading}
              className={loading ? 'btn-loading' : ''}
            >
              {loading
                ? t('contact_page.form.processing')
                : t('contact_page.form.submit')}
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}

export default Contact
